import { PencilIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { PHRASE_TYPES } from '@/entities/conversations';
import { formatTimeIntervalMs } from '@/shared/lib';
import { IconButton } from '@/shared/ui';
import { getRoleSide } from '../speaker';
import { describePhrase, getPhraseSpeakerLabel } from './recordLabels';
import { TranscriptDeleteRecordButton } from './TranscriptDeleteRecordButton';
import { useTranscriptEditorContext } from './TranscriptEditorContext';
import { TranscriptListenButton } from './TranscriptListenButton';
import { type TranscriptRecordDetail, TranscriptRecordView } from './TranscriptRecordView';
import type { EditableTranscriptRecord } from './types';

type Props = {
  record: EditableTranscriptRecord;
};

/**
 * Строка записи вне правки: подписи записи и её действия. Пока поле перебивания ждёт выбора,
 * строка реплики выбирается нажатием.
 */
export const TranscriptRecordItem: FC<Props> = ({ record }) => {
  const { t } = useTranslation(['dialogs', 'common']);
  const { phrasesById, timeFormat, openedForm, isDirty, phrasePick, openForm } = useTranscriptEditorContext();

  const finishedAt = record.finishedAt ?? record.startedAt;
  const formatTime = (ms: number) => formatTimeIntervalMs(ms, timeFormat);
  const timeRange = `${formatTime(record.startedAt)} – ${formatTime(finishedAt)}`;

  const describeLinkedPhrase = (phraseId: number | null | undefined) => {
    const phrase = phraseId == null ? undefined : phrasesById.get(phraseId);
    return phrase ? describePhrase(phrase, t) : t('dialogs:transcriptEdit.phraseNotFound');
  };
  const details: TranscriptRecordDetail[] | undefined =
    record.type === PHRASE_TYPES.INTERRUPTION
      ? [
          {
            label: t('dialogs:transcriptEdit.interruptedPhrase'),
            value: describeLinkedPhrase(record.interruptedPhraseId),
          },
          {
            label: t('dialogs:transcriptEdit.interruptionPhrase'),
            value: describeLinkedPhrase(record.interruptionPhraseId),
          },
        ]
      : undefined;

  const isPhrase = record.type === PHRASE_TYPES.PHRASE;
  const title = isPhrase ? getPhraseSpeakerLabel(record, t) : t(`dialogs:transcriptEdit.recordType.${record.type}`);

  const onPick = phrasePick && isPhrase ? () => phrasePick.onPick(record.id) : undefined;

  // Открыть другую запись можно, пока в открытой нет несохранённых правок; удалять — только
  // когда ничего не правится: удаление реплики задело бы перебивание, открытое на правку
  const actions = !onPick && (
    <>
      <TranscriptListenButton type={record.type} interval={{ startedAt: record.startedAt, finishedAt }} />
      <IconButton
        size="icon"
        icon={<PencilIcon />}
        label={t(`dialogs:transcriptEdit.editRecord.${record.type}`)}
        disabled={isDirty}
        onClick={() => openForm({ mode: 'edit', recordId: record.id })}
      />
      <TranscriptDeleteRecordButton record={record} disabled={openedForm !== null} />
    </>
  );

  return (
    <TranscriptRecordView
      recordId={record.id}
      title={title}
      timeRange={timeRange}
      side={isPhrase && record.person ? getRoleSide(record.person.role) : undefined}
      text={isPhrase ? (record.text ?? '') : undefined}
      details={details}
      actions={actions}
      onPick={onPick}
    />
  );
};
